import { z } from 'zod';
import { ParentRole, College, StudentGender } from '../../entities/user/enums';
import { NotificationTypes } from '../../entities/notifications';
import { QuestionType } from '../../entities/quiz/enums';
import { QuizResultsSchema } from '../../outputs/quiz/schemas';

export const RefreshAccessTokenInputSchema = z.object({
    accessToken: z.string(),
});

export const UpdateCurrentUserDataInputSchema = z.object({
    firstName: z.string().optional(),
    lastName: z.string().optional(),
    parentRole: z.nativeEnum(ParentRole).optional(),
    stateOfResidence: z.string().optional(),
    college: z.nativeEnum(College).optional(),
    numberOfChildren: z.number().int().positive().optional(),
    studentGender: z.nativeEnum(StudentGender).optional(),
    studentAge: z.number().int().positive().optional(),
    studentFirstName: z.string().optional(),
    studentPhoneLastFour: z
        .string()
        .length(4)
        .regex(/^\d{4}$/)
        .optional(),
    avatarFilename: z.string().optional(),
    onboardingCompleted: z.boolean().optional(),
    notificationPermissions: z
        .record(z.nativeEnum(NotificationTypes), z.boolean())
        .optional(),
});

export const UpdateOnboardingInputSchema = z.object({
    firstName: z.string().optional(),
    lastName: z.string().optional(),
    parentRole: z.nativeEnum(ParentRole).optional(),
    stateOfResidence: z.string().optional(),
    college: z.nativeEnum(College).optional(),
    numberOfChildren: z.number().int().positive().optional(),
    studentGender: z.nativeEnum(StudentGender).optional(),
    studentAge: z.number().int().positive().optional(),
    studentFirstName: z.string().optional(),
    studentPhoneLastFour: z
        .string()
        .length(4)
        .regex(/^\d{4}$/)
        .optional(),
    avatarFilename: z.string().optional(),
    onboardingCompleted: z.boolean().optional(),
    notificationPermissions: z
        .record(z.nativeEnum(NotificationTypes), z.boolean())
        .optional(),
});

// Quiz answer value schemas by type
const BooleanAnswerValueSchema = z.object({
    value: z.boolean(),
});

const NumericAnswerValueSchema = z.object({
    value: z.number(),
});

const PercentageAnswerValueSchema = z.object({
    value: z.number().min(0).max(100),
});

const SingleChoiceAnswerValueSchema = z.object({
    value: z.string(),
});

// Union of all answer types
const QuizAnswerValueSchema = z.union([
    BooleanAnswerValueSchema,
    NumericAnswerValueSchema,
    PercentageAnswerValueSchema,
    SingleChoiceAnswerValueSchema,
]);

export const SubmitQuizAnswerInputSchema = z.object({
    quizId: z.string(),
    questionId: z.string(),
    answer: QuizAnswerValueSchema,
});

export const SubmitQuizAnswerResponseSchema = z.object({
    isCompleted: z.boolean(),
    answeredQuestionsCount: z.number(),
    totalQuestionsCount: z.number(),
    results: QuizResultsSchema.optional(),
});

export const ResetQuizResponsesInputSchema = z.object({
    quizId: z.string(),
});

export const ResetQuizResponsesResponseSchema = z.object({
    success: z.boolean(),
});

export const GetQuizResultsInputSchema = z.object({
    quizId: z.string(),
});

export const GetQuizResultsResponseSchema = QuizResultsSchema;

export const MarkResultsSeenInputSchema = z.object({
    quizId: z.string(),
});

export const MarkResultsSeenResponseSchema = z.object({
    success: z.boolean(),
});

// ─── Push Token Schemas ──────────────────────────────────────────────────────

export const RegisterPushTokenInputSchema = z.object({
    token: z.string().min(1),
    deviceId: z.string().min(1),
});

export const UnregisterPushTokenInputSchema = z.object({
    deviceId: z.string().min(1),
});
